import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Divider } from '@material-ui/core';
import CountUp from 'react-countup';

import { AnimatedProgress, ConditionalTooltip } from '../../../../components';
import forceFloat from '../../../../util/force-float';

import './hierarchy-item.css';

const COUNTUP_DURATION = 1;

const styles = {
  header: {
    cursor: 'pointer',
  },
  value: {
    flexGrow: 1,
    textAlign: 'right',
    paddingRight: '5px',
  },
  values: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    fontSize: '0.75rem',
  },
  comparisonValue: {
    color: '#b4b4b4',
    fontSize: '0.7rem',
  },
  titleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '10px 0 15px',
  },
  progress: {
    height: '4px',
    background: 'none',
  },
  collapse: {
    marginLeft: '15px',
  },
  tooltip: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
  },
};

const barColors = [
  'barColor1',
  'barColor2',
  'barColor3',
];

const formatDigitsIfNumber = (value, digits) => {
  if (!digits && digits !== 0) {
    return value;
  }

  return value.toFixed(digits);
};

const Title = ({ data, title }) => {
  if (!title || title.length < 1) {
    return (
      <span>
        {data.name}
      </span>
    );
  }

  const titleItems = title.map(titleItem => (
    <span key={titleItem.attribute} style={titleItem.style}>
      {`${titleItem.prefix || ''}${formatDigitsIfNumber(data[titleItem.attribute], titleItem.digits)}${titleItem.postfix || ' '}`}
    </span>
  ));

  return (
    <div>
      {titleItems}
    </div>
  );
};

Title.propTypes = {
  data: PropTypes.object.isRequired,
  title: PropTypes.array,
};

Title.defaultProps = {
  title: null,
};

const HierarchyItem = (props) => {
  const [isCollapsed, setCollapsed] = useState(true);

  const {
    data, progress, total, level, title, itemProgressAttribute, showGroupDetails, hideProgress, currencySymbol,
  } = props;

  const barColor = barColors[level % barColors.length];

  const children = showGroupDetails !== false && data.values
    ? data.values.map((d, i) => {
      const key = d.name + i;

      return (
        <HierarchyItem
          key={key}
          title={title}
          data={d}
          progress={forceFloat(d[itemProgressAttribute]) / total * 100}
          total={total}
          itemProgressAttribute={itemProgressAttribute}
          level={level + 1}
          columns={data.columns}
          hideProgress={hideProgress}
          currencySymbol={currencySymbol}
        />
      );
    })
    : null;

  const comparisonValues = data.comparisons ? data.comparisons.map(comparisons => comparisons[itemProgressAttribute]) : [];

  const comparisonItems = comparisonValues.map((cv, i) => {
    const nextItemProgressAttribute = data[itemProgressAttribute] ? itemProgressAttribute : 1;

    const trendNumber = (100 - (data[nextItemProgressAttribute] / cv * 100)) * -1;
    let trend = null;

    if (!Number.isNaN(trendNumber) && Number.isFinite(trendNumber) && trendNumber !== 0) {
      trend = (
        <CountUp
          end={trendNumber}
          suffix="%"
          className={trendNumber > 0 ? 'positive' : 'negative'}
          prefix={trendNumber > 0 ? '+' : ''}
          duration={COUNTUP_DURATION}
        />
      );
    } else {
      trend = '-';
    }

    return (
      // eslint-disable-next-line react/no-array-index-key
      <div key={`${nextItemProgressAttribute} + ${i}`}>
        <div style={styles.titleWrapper}>
          <div style={styles.comparisonValue}>
            {`Vergleichszeitraum ${i + 1}`}
          </div>
          <div>
            <CountUp
              end={cv}
              suffix={currencySymbol}
              decimals={2}
              decimal=","
              duration={COUNTUP_DURATION}
              style={styles.comparisonValue}
            />
            <span style={styles.comparisonValue}>
              {' ('}
              {trend}
              {') '}
            </span>
          </div>

        </div>
        {!hideProgress
        && (
          <AnimatedProgress
            variant="determinate"
            value={cv / total * 100}
            classes={{ barColorPrimary: barColor }}
            style={styles.progress}
          />

        )
        }
      </div>
    );
  });

  return (
    <div>
      <ConditionalTooltip
        title={!hideProgress && `Umsatz gesamt: ${total.toFixed(2)} ${currencySymbol}`}
        style={styles.tooltip}
      >
        <div
          onClick={() => setCollapsed(!isCollapsed)}
          onKeyDown={() => setCollapsed(!isCollapsed)}
          style={children ? styles.header : undefined}
          className={children ? 'hover' : undefined}
        >
          <div style={styles.titleWrapper}>
            <Title title={title} data={data} />
            <CountUp
              end={data[itemProgressAttribute]}
              suffix={currencySymbol}
              decimals={2}
              decimal=","
              duration={COUNTUP_DURATION}
              style={styles.value}
            />
          </div>
          {!hideProgress && (
            <AnimatedProgress
              variant="determinate"
              value={progress}
              classes={{ barColorPrimary: barColor }}
              style={styles.progress}
            />
          )
          }
          {comparisonItems}
          <Divider style={styles.divider} />
        </div>
      </ConditionalTooltip>
      <Collapse in={!isCollapsed} unmountOnExit style={styles.collapse}>
        {children}
      </Collapse>

    </div>
  );
};

HierarchyItem.propTypes = {
  currencySymbol: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  hideProgress: PropTypes.bool,
  itemProgressAttribute: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  level: PropTypes.number,
  progress: PropTypes.number,
  showGroupDetails: PropTypes.bool,
  title: PropTypes.array,
  total: PropTypes.number.isRequired,
};

HierarchyItem.defaultProps = {
  data: {},
  hideProgress: false,
  level: 0,
  showGroupDetails: true,
  title: null,
  progress: 0,
};

export default HierarchyItem;
